import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { ENTITY_CONFIG } from "@/lib/entityConfig";
import { formatCurrency } from "@/lib/formatters";
import AddEditDialog from "@/components/shared/AddEditDialog";
import UploadDialog from "@/components/shared/UploadDialog";
import DeleteConfirmDialog from "@/components/shared/DeleteConfirmDialog";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Upload, Pencil, Trash2, Database } from "lucide-react";

const ENTITY_TYPES = ["properties", "funds", "managers", "sectors", "geography", "benchmarks", "risk_scenarios", "cashflows"];

function EntityTable({ entityType }) {
  const config = ENTITY_CONFIG[entityType];
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get(`/${config.apiPath}`);
      const items = res.data[config.responseKey] || [];
      setData(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [config.apiPath, config.responseKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refresh = () => fetchData();
  const displayFields = config.fields.slice(0, 7);

  const formatVal = (field, val) => {
    if (val == null) return "--";
    if (field.type === "number" && val > 100000) return formatCurrency(val);
    return String(val);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-data text-muted-foreground">{data.length} records</span>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowAdd(true)} className="gap-1" data-testid={`dm-add-${entityType}`}>
            <Plus size={14} />Add {config.label.replace(/s$/, "")}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setShowUpload(true)} className="gap-1" data-testid={`dm-upload-${entityType}`}>
            <Upload size={14} />Upload CSV/Excel
          </Button>
        </div>
      </div>

      <Card className="border border-border bg-card">
        <ScrollArea className="h-[500px]">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                {displayFields.map((f) => (
                  <TableHead key={f.key} className="text-xs uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap">
                    {f.label}
                  </TableHead>
                ))}
                <TableHead className="text-right text-xs uppercase tracking-wider font-semibold text-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row, idx) => (
                <TableRow key={row.id || idx} className="border-border hover:bg-muted/50 transition-colors">
                  {displayFields.map((f) => (
                    <TableCell key={f.key} className="text-sm font-data whitespace-nowrap">
                      {formatVal(f, row[f.key])}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        className="p-1.5 rounded-sm hover:bg-secondary transition-colors"
                        onClick={() => setEditItem(row)}
                        data-testid={`dm-edit-${entityType}-${idx}`}
                      >
                        <Pencil size={14} className="text-muted-foreground hover:text-foreground" />
                      </button>
                      <button
                        className="p-1.5 rounded-sm hover:bg-destructive/20 transition-colors"
                        onClick={() => setDeleteItem(row)}
                        data-testid={`dm-delete-${entityType}-${idx}`}
                      >
                        <Trash2 size={14} className="text-muted-foreground hover:text-red-500" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={displayFields.length + 1} className="text-center text-muted-foreground py-12">
                    No records. Click "Add" or "Upload" to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>

      <AddEditDialog
        open={showAdd || !!editItem}
        item={editItem}
        entityType={entityType}
        onClose={() => { setShowAdd(false); setEditItem(null); }}
        onSave={refresh}
      />
      <UploadDialog
        open={showUpload}
        entityType={entityType}
        onClose={() => setShowUpload(false)}
        onSuccess={refresh}
      />
      <DeleteConfirmDialog
        open={!!deleteItem}
        item={deleteItem}
        entityType={entityType}
        onClose={() => setDeleteItem(null)}
        onDelete={refresh}
      />
    </div>
  );
}

export default function DataManagement() {
  return (
    <div className="p-6 space-y-6" data-testid="data-management-page">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-sm bg-primary/10">
          <Database size={20} className="text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Data Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Add, edit, delete, and bulk upload data across all entities</p>
        </div>
      </div>

      <Tabs defaultValue="properties" data-testid="dm-tabs">
        <TabsList className="bg-secondary border border-border flex-wrap h-auto p-1 gap-1">
          {ENTITY_TYPES.map((et) => (
            <TabsTrigger key={et} value={et} className="text-xs" data-testid={`dm-tab-${et}`}>
              {ENTITY_CONFIG[et].label}
            </TabsTrigger>
          ))}
        </TabsList>
        {ENTITY_TYPES.map((et) => (
          <TabsContent key={et} value={et} className="mt-4">
            <EntityTable entityType={et} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
